import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDialog } from "@shared/features/dialog/useDialog";
import { Loader } from "@shared/ui/Loader";
import { marketingEventBus } from "../../marketing.events";
import { Invitation } from "@platform/types/platform.types";
import { validateOrgInvitation } from "@platform/features/orgInvitation/utils/validateOrgInvitation";
import {
  INVITATION_SET,
  usePlatformDispatch,
} from "@platform//PlatformContext";
import { MarketingHeroInvitation } from "./MarketingHeroInvitation";
import { MarketingHeroNoInvitation } from "./MarketingHeroNoInvitation";

export function MarketingHeroContainer() {
  const navigate = useNavigate();

  const dispatch = usePlatformDispatch();

  const [isValidatingCode, setIsValidatingCode] = useState<boolean>(false);

  const [isInvitation, setIsInvitation] = useState<boolean>(false);

  const [inviterName, setInviterName] = useState<string>("");

  const [invitation, setInvitation] = useState<Invitation | null>(null);

  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");

  useEffect(() => {
    if (!code) return;

    setIsValidatingCode(true);
  }, [code]);

  useEffect(() => {
    if (!code) return;

    validateOrgInvitation({ code, type: "code" })
      .then((invitation) => {
        setInvitation(invitation);

        // emit event
        marketingEventBus.emitEvent("marketing:invitation:direct:viewed", {
          invitation: {
            id: invitation.id,
            code: invitation.code,
            is_valid: invitation.is_valid,
            is_used: invitation.is_used,
            organization: {
              id: invitation.organization?.id,
              name: invitation.organization?.name,
            },
          },
        });

        dispatch({ type: INVITATION_SET, payload: invitation });

        setIsValidatingCode(false);

        setIsInvitation(true);

        const inviterName = invitation.inviter_user
          ? [
              invitation.inviter_user.first_name,
              invitation.inviter_user.last_name,
            ].join(" ")
          : invitation.organization?.name;

        setInviterName(inviterName);
      })
      .catch(() => {
        setIsValidatingCode(false);
        setIsInvitation(false);
      });
  }, [code]);

  const handleStart = (invitation: Invitation | null) => {
    if (!invitation) return;

    // emit event
    marketingEventBus.emitEvent("marketing:invitation:direct:started", {
      invitation: {
        id: invitation.id,
        code: invitation.code,
        is_valid: invitation.is_valid,
        is_used: invitation.is_used,
        organization: {
          id: invitation.organization?.id,
          name: invitation.organization?.name || "",
        },
      },
    });

    // navigate to the onboarding page
    navigate("/enroll");
  };

  const { openDialog } = useDialog();

  if (isValidatingCode) return <Loader />;

  return (
    <>
      {isInvitation ? (
        <MarketingHeroInvitation
          inviterName={inviterName}
          onStart={() => handleStart(invitation)}
        />
      ) : (
        <MarketingHeroNoInvitation
          onCodeLinkClick={() => openDialog("code")}
          onOrgLookupClick={() => openDialog("orgLookup")}
          onNewsletterClick={() => openDialog("newsletter")}
          onLoginClick={() => navigate("/login")}
        />
      )}
    </>
  );
}

export default MarketingHeroContainer;
